import React, { useEffect, useLayoutEffect, useRef, useState } from 'react'
import { AnimatePresence, motion } from 'framer-motion'
import { Megaphone, Sparkles, X, ArrowUpRight } from 'lucide-react'
import { Link } from 'react-router-dom'
import styles from './PromoBanner.module.css'
import { getActiveAnnouncements, markAnnouncementRead } from '../lib/announcements'

// Banner surfaced on marketing & pricing pages. For signed-in users it carries
// their latest unread admin announcement (audience-scoped). For visitors, or
// when no announcement is live, it falls back to the marketing config below.
// Dismissal marks the announcement read (per user) or is remembered per
// visitor in localStorage for the marketing fallback.
const KEY = 'opraiz.promoDismissed'

const ACTIVE_PROMO = {
  message: 'Launch offer — 20% off annual plans',
  sub: 'Add code WELCOME20 at checkout',
  link: '/pricing',
  cta: 'View plans',
  expiresAt: '2026-12-31T23:59:59Z',
}

const PRIORITY_RANK = { urgent: 0, high: 1, normal: 2, low: 3 }

const PromoBanner = () => {
  // Read the dismissal flag during the FIRST render, not in an effect. Deciding
  // after mount meant the banner was inserted a frame late and pushed the whole
  // page down; deciding here reserves its row in the very first paint.
  const [show, setShow] = useState(() => {
    if (typeof window === 'undefined') return false
    try {
      if (localStorage.getItem(KEY) === '1') return false
    } catch (e) {
      console.warn('[promo] failed reading local storage:', e)
    }
    if (ACTIVE_PROMO.expiresAt && new Date(ACTIVE_PROMO.expiresAt) < new Date()) return false
    return true
  })
  const [announcement, setAnnouncement] = useState(null)
  const wrapRef = useRef(null)

  // Pull the most relevant unread announcement for the signed-in user. On any
  // failure (including unauthenticated visitors) the marketing fallback stands.
  useEffect(() => {
    let active = true
    getActiveAnnouncements()
      .then((res) => {
        if (!active) return
        const unread = (res?.announcements || []).filter((a) => !a.read)
        if (unread.length) {
          setAnnouncement(
            unread.sort(
              (a, b) => (PRIORITY_RANK[a.priority] ?? 9) - (PRIORITY_RANK[b.priority] ?? 9)
            )[0]
          )
          setShow(true)
        }
      })
      .catch((e) => console.warn('[promo] failed loading announcements:', e))
    return () => {
      active = false
    }
  }, [])

  // Keep the fixed navbar in sync so it never overlaps the banner. Expose the
  // banner's measured height as --promo-offset; cleared when hidden/unmounted.
  // Layout effect, not effect: the navbar is position:fixed and reads this
  // variable as its `top`, so setting it in a passive effect moved the navbar
  // after paint and cost a layout shift on every marketing route.
  useLayoutEffect(() => {
    const el = wrapRef.current
    if (!show || !el) return undefined
    const update = () => {
      document.documentElement.style.setProperty('--promo-offset', `${el.offsetHeight}px`)
    }
    update()
    const ro = new ResizeObserver(update)
    ro.observe(el)
    return () => {
      ro.disconnect()
      document.documentElement.style.removeProperty('--promo-offset')
    }
  }, [show])

  const dismiss = () => {
    setShow(false)
    if (announcement) {
      try {
        markAnnouncementRead(announcement.id)
      } catch (e) {
        console.warn('[promo] failed marking announcement read:', e)
      }
    } else {
      try {
        localStorage.setItem(KEY, '1')
      } catch (e) {
        console.warn('[promo] failed writing local storage:', e)
      }
    }
  }

  const item = announcement || ACTIVE_PROMO

  return (
    <AnimatePresence>
      {show && (
        <motion.div
          ref={wrapRef}
          className={styles.banner}
          role="region"
          aria-label="Site announcement"
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -10 }}
          transition={{ duration: 0.3, ease: 'easeOut' }}
          onKeyDown={(e) => {
            if (e.key === 'Escape') dismiss()
          }}
        >
          <div className={`container ${styles.inner}`}>
            <p className={styles.content} role="status">
              {announcement ? (
                <Megaphone size={15} className={styles.icon} aria-hidden="true" />
              ) : (
                <Sparkles size={15} className={styles.icon} aria-hidden="true" />
              )}
              <span className={styles.message}>
                {announcement ? announcement.title : ACTIVE_PROMO.message}
              </span>
              <span className={styles.sub}>
                {announcement ? announcement.body || '' : ACTIVE_PROMO.sub}
              </span>
            </p>
            <div className={styles.actions}>
              {announcement?.link && (
                <Link to={announcement.link} className={styles.cta}>
                  View <ArrowUpRight size={13} aria-hidden="true" />
                </Link>
              )}
              {!announcement && (
                <Link to={item.link} className={styles.cta}>
                  {item.cta} <ArrowUpRight size={13} aria-hidden="true" />
                </Link>
              )}
              <button
                type="button"
                className={styles.close}
                onClick={dismiss}
                aria-label="Dismiss promotion"
              >
                <X size={16} aria-hidden="true" />
              </button>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  )
}

export default PromoBanner
